import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import Layout from '@/components/Layout';
import { COMPANIES, companyPath } from '@/lib/index';
import { Link } from 'react-router-dom';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[50vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1698303098477-fac2428d9dfe?w=1600&q=85"
            alt="Contact"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.5)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-5">Reach Us</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-3xl">
              Let's <span className="text-gold italic">Connect</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Contact grid */}
      <section className="py-20 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Left info col */}
            <div className="lg:col-span-2 bg-[#0c0c0c] border border-[rgba(201,168,76,0.08)] p-10 lg:p-14 flex flex-col justify-between">
              <div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 }}
                >
                  <p className="eyebrow mb-4">Get in Touch</p>
                  <h2 className="heading-display text-3xl text-[#f5f5f5] mb-6">
                    Work with the <br /> ALV Ecosystem
                  </h2>
                  <div className="divider-gold-left" />
                  <p className="text-[#f5f5f5]/50 text-sm leading-relaxed mt-6 mb-10">
                    Whether you're an aspiring talent, a brand partner, event organizer, or media professional — we'd love to hear from you.
                  </p>
                </motion.div>

                <div className="space-y-6">
                  {[
                    { Icon: Mail, label: 'Email', value: 'hello@alvpageantcircle.com' },
                    { Icon: Phone, label: 'Phone', value: '+63 (2) 1234 5678' },
                    { Icon: MapPin, label: 'Address', value: 'BGC, Taguig City\nMetro Manila, Philippines' },
                  ].map(({ Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center border border-[rgba(201,168,76,0.3)] flex-shrink-0 mt-0.5">
                        <Icon size={14} className="text-[#c9a84c]" />
                      </div>
                      <div>
                        <p className="text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/50 mb-0.5">{label}</p>
                        <p className="text-sm text-[#f5f5f5]/70 whitespace-pre-line">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Companies quick links */}
              <div className="mt-12 pt-8 border-t border-[rgba(201,168,76,0.1)]">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/40 mb-4">Contact Our Companies</p>
                <div className="space-y-2">
                  {COMPANIES.map(c => (
                    <Link
                      key={c.id}
                      to={companyPath(c.slug) + '/contact'}
                      className="flex items-center justify-between text-[10px] tracking-[0.1em] uppercase text-[#f5f5f5]/40 hover:text-[#c9a84c] transition-colors duration-200 py-1"
                    >
                      <span>{c.shortName}</span>
                      <span className="text-[#c9a84c]/30">→</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Right form col */}
            <div className="lg:col-span-3 bg-[#090909] border border-[rgba(201,168,76,0.06)] border-l-0 p-10 lg:p-14">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center py-20"
                >
                  <div className="w-16 h-16 border-2 border-[#c9a84c] flex items-center justify-center mb-6">
                    <span className="text-[#c9a84c] text-2xl">✓</span>
                  </div>
                  <p className="eyebrow mb-4">Message Received</p>
                  <h3 className="heading-display text-3xl text-[#f5f5f5] mb-4">Thank You</h3>
                  <p className="text-[#f5f5f5]/50 text-sm max-w-xs leading-relaxed">
                    We'll review your message and be in touch within 2–3 business days.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-7">
                  <p className="eyebrow mb-2">Send a Message</p>
                  <h3 className="heading-display text-2xl text-[#f5f5f5] mb-6">How Can We Help You?</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Your full name' },
                      { name: 'email', label: 'Email Address', type: 'email', placeholder: 'your@email.com' },
                    ].map(field => (
                      <div key={field.name}>
                        <label className="block text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-2">{field.label}</label>
                        <input
                          type={field.type}
                          name={field.name}
                          value={form[field.name as keyof typeof form]}
                          onChange={handleChange}
                          placeholder={field.placeholder}
                          required
                          className="w-full bg-transparent border border-[rgba(201,168,76,0.2)] text-[#f5f5f5] text-sm px-4 py-3.5 placeholder-[rgba(245,245,245,0.2)] focus:outline-none focus:border-[rgba(201,168,76,0.6)] transition-colors duration-300"
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-2">Inquiry Type</label>
                    <select
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full bg-[#0c0c0c] border border-[rgba(201,168,76,0.2)] text-[#f5f5f5] text-sm px-4 py-3.5 focus:outline-none focus:border-[rgba(201,168,76,0.6)] transition-colors duration-300"
                    >
                      <option value="">Select an inquiry type</option>
                      <option value="talent">Talent Application</option>
                      <option value="brand">Brand Partnership</option>
                      <option value="events">Events & Production</option>
                      <option value="advertising">Advertising & Content</option>
                      <option value="pageant">Pageant Registration</option>
                      <option value="media">Media & Press</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[9px] tracking-[0.2em] uppercase text-[#c9a84c]/60 mb-2">Your Message</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Tell us about your inquiry or project..."
                      required
                      className="w-full bg-transparent border border-[rgba(201,168,76,0.2)] text-[#f5f5f5] text-sm px-4 py-3.5 placeholder-[rgba(245,245,245,0.2)] focus:outline-none focus:border-[rgba(201,168,76,0.6)] transition-colors duration-300 resize-none"
                    />
                  </div>

                  <button type="submit" className="btn-gold-fill w-full md:w-auto">
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
